import React, { useEffect, useState } from 'react';
import { DashBoardCommonGrid } from '../../DashBoardNew/dashBoardNewStyles';
import { CommonGrid, CommonFlex } from 'styledComponents/common';
import RevenueCompareChart from './RevenueCompareChart';
import SmallInfoCard from './SmallInfoCard';
import { currencySymbolMap } from '../../../utils/currencies';


const DashBoardThirdSection = ({
  getDashboardRevenue,
  revenueIsLoading,
  lastYearRevenue,
  thisYearRevenue,
  currentMonthIndex,
  selectedOrganization,
  getDashboardOrganizationSales,
  organizationSalesIsLoading,
  organizationSalesData,
}) => {
  const [cardsData, setCardsData] = useState([]);
  const currency = localStorage.getItem('currency') || 'USD';
  const currencySymbol = currencySymbolMap[currency] || '$';

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return `${currencySymbol}0`;
    }

    try {
      // Try to use Intl.NumberFormat with the currency code
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return formatted;
    } catch (error) {
      // Fallback: manually format with symbol if Intl doesn't support the currency
      const formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
      return `${currencySymbol}${formattedValue}`;
    }
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return null;
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Determine delta type based on percentage change
  const getDeltaType = (percentageChange) => {
    if (percentageChange === null || percentageChange === undefined) return null;
    return percentageChange >= 0 ? 'increase' : 'decrease';
  };

  // Transform API data to card format
  const transformSalesData = (apiData) => {
    // Always return 6 cards with default values of 0 if data is missing
    const cards = [];

    // Sales Year
    cards.push({
      title: "Sales This Year",
      amount: apiData?.sales_year?.this_year !== undefined && apiData?.sales_year?.this_year !== null
        ? formatCurrency(apiData.sales_year.this_year)
        : formatCurrency(0),
      delta: apiData?.sales_year?.percentage_change !== undefined && apiData?.sales_year?.percentage_change !== null
        ? formatPercentage(apiData.sales_year.percentage_change)
        : null,
      deltaType: apiData?.sales_year?.percentage_change !== undefined && apiData?.sales_year?.percentage_change !== null
        ? getDeltaType(apiData.sales_year.percentage_change)
        : null,
      subText: "vs last year",
    });

    // Sales Month
    cards.push({
      title: "Sales This Month",
      amount: apiData?.sales_month?.this_month !== undefined && apiData?.sales_month?.this_month !== null
        ? formatCurrency(apiData.sales_month.this_month)
        : formatCurrency(0),
      delta: apiData?.sales_month?.percentage_change !== undefined && apiData?.sales_month?.percentage_change !== null
        ? formatPercentage(apiData.sales_month.percentage_change)
        : null,
      deltaType: apiData?.sales_month?.percentage_change !== undefined && apiData?.sales_month?.percentage_change !== null
        ? getDeltaType(apiData.sales_month.percentage_change)
        : null,
      subText: "vs last month",
    });

    // Average Sales Month
    cards.push({
      title: "Average Sales Per Month",
      amount: apiData?.average_sales_month?.this_year !== undefined && apiData?.average_sales_month?.this_year !== null
        ? formatCurrency(apiData.average_sales_month.this_year)
        : formatCurrency(0),
      delta: apiData?.average_sales_month?.percentage_change !== undefined && apiData?.average_sales_month?.percentage_change !== null
        ? formatPercentage(apiData.average_sales_month.percentage_change)
        : null,
      deltaType: apiData?.average_sales_month?.percentage_change !== undefined && apiData?.average_sales_month?.percentage_change !== null
        ? getDeltaType(apiData.average_sales_month.percentage_change)
        : null,
      subText: "this year",
    });

    // Average Sales Job
    cards.push({
      title: "Average Sales Per Job",
      amount: apiData?.average_sales_job?.this_month !== undefined && apiData?.average_sales_job?.this_month !== null
        ? formatCurrency(apiData.average_sales_job.this_month)
        : formatCurrency(0),
      delta: apiData?.average_sales_job?.percentage_change !== undefined && apiData?.average_sales_job?.percentage_change !== null
        ? formatPercentage(apiData.average_sales_job.percentage_change)
        : null,
      deltaType: apiData?.average_sales_job?.percentage_change !== undefined && apiData?.average_sales_job?.percentage_change !== null
        ? getDeltaType(apiData.average_sales_job.percentage_change)
        : null,
      subText: "this month",
    });

    // Missed Sales
    cards.push({
      title: "Missed Sales",
      amount: apiData?.missed_sales?.this_month !== undefined && apiData?.missed_sales?.this_month !== null
        ? formatCurrency(apiData.missed_sales.this_month)
        : formatCurrency(0),
      delta: apiData?.missed_sales?.percentage_change !== undefined && apiData?.missed_sales?.percentage_change !== null
        ? formatPercentage(apiData.missed_sales.percentage_change)
        : null,
      deltaType: apiData?.missed_sales?.percentage_change !== undefined && apiData?.missed_sales?.percentage_change !== null
        ? getDeltaType(apiData.missed_sales.percentage_change)
        : null,
      subText: "this month",
    });

    // Average Missed Sales
    cards.push({
      title: "Average Missed Sales",
      amount: apiData?.average_missed_sales?.this_year !== undefined && apiData?.average_missed_sales?.this_year !== null
        ? formatCurrency(apiData.average_missed_sales.this_year)
        : formatCurrency(0),
      delta: apiData?.average_missed_sales?.percentage_change !== undefined && apiData?.average_missed_sales?.percentage_change !== null
        ? formatPercentage(apiData.average_missed_sales.percentage_change)
        : null,
      deltaType: apiData?.average_missed_sales?.percentage_change !== undefined && apiData?.average_missed_sales?.percentage_change !== null
        ? getDeltaType(apiData.average_missed_sales.percentage_change)
        : null,
      subText: "this year",
    });

    return cards;
  };

  // Call API when organization changes
  useEffect(() => {
    if (selectedOrganization?.id && getDashboardOrganizationSales) {
      const payload = {
        organization_id: selectedOrganization.id,
      };
      getDashboardOrganizationSales(payload);
    }
  }, [selectedOrganization, getDashboardOrganizationSales]);

  // Update cards data when API data changes
  useEffect(() => {
    // Always show 6 cards with default values of 0 if no data
    const transformedData = transformSalesData(organizationSalesData || {});
    setCardsData(transformedData);
  }, [organizationSalesData]);

  return (
    <DashBoardCommonGrid responsiveBreakpoint="768px" responsiveColumns="1fr" columns="1fr 1fr" gap="20px">
      <CommonGrid columns="1fr" gap="20px">
        {/* Revenue compare chart Section */}
        <RevenueCompareChart
          getDashboardRevenue={getDashboardRevenue}
          revenueIsLoading={revenueIsLoading}
          selectedOrganization={selectedOrganization}
          lastYearRevenue={lastYearRevenue}
          thisYearRevenue={thisYearRevenue}
          currentMonthIndex={currentMonthIndex}
        />
      </CommonGrid>

      <CommonGrid columns="1fr 1fr" gap="20px">
        {/* left column: 3 small cards */}
        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(0, 3).map((item, index) => (
            <SmallInfoCard
              key={index}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationSalesIsLoading}
            />
          ))}
        </CommonGrid>

        {/* right column: 3 small cards */}
        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(3, 6).map((item, index) => (
            <SmallInfoCard
              key={index + 3}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationSalesIsLoading}
            />
          ))}
        </CommonGrid>
      </CommonGrid>
    </DashBoardCommonGrid>
  );
};

export default DashBoardThirdSection;